import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Box, Grid, Typography } from '@mui/material';
import { Elabels, initialCaseFormValues } from '../helpers';
import { StyledValueFormHeader } from '../styles';
import { StyledInternalDescription } from './styles';
import { FormInput } from 'components/molecules/FormInput';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { FormSelect } from 'components/molecules/FormSelect';
import { findInDictionary, mapDictionaryToOptionProp } from 'helpers/dictionary';
import { FileDropzoneContainer } from 'components/molecules/FormInputFile';
import { EMIMETypes } from 'constants/MIMETypes';
import { Resolver, useForm } from 'react-hook-form';
import { ICaseFormValues, IWeaponLicenceTemplates } from 'models/cases';
import { useDictionary } from 'models/dictionaryContext';
import { yupResolver } from '@hookform/resolvers/yup';
import useFormSchema from './schema';
import { useCase } from '../hooks';
import { useScroll } from 'hooks/useScroll';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { getLegalEntity, getOrgLegalEntity } from 'requests/legalEntities';
import { Loader } from 'components/atoms/Loader';
import { IFormElementOptionProp } from 'models/form';
import { FormAutocomplete } from 'components/molecules/FormAutocomplete';
import { getHtmlText } from 'components/organisms/HtmlSlate';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { useRenewLicence } from 'pages/LegalEntitiesPage/LegalEntityLicenceRenewPage/hooks';
import { ECaseProcessTypes } from 'constants/CaseProcessTypes';
import { useVaryLicence } from 'pages/LegalEntitiesPage/LegalEntityLicenceVaryPage/hooks';
import { IRenewData, IVaryData, IVaryDataVariations } from 'models/licence';
import { Table } from 'components/molecules/Table';
import { mapVariationsToDataSource, variationsTableColumns } from './helpers';
import { DocumentsForm } from './documentsForm';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { formatDate } from 'utils/date';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { useConfigurationLicence } from 'pages/LicenceConfigurationPage/hooks';
import { useLang } from 'models/langContext';
import { useAccount } from 'pages/AccountPage/hooks';
import { useParams } from 'react-router-dom';
import { ECaseTaskNames } from 'constants/CaseTaskNames';
import { FormSearchSelect } from 'components/molecules/FormSearchSelect';

export enum EAddCaseType {
  APPLICATION = 'APPLICATION',
  RENEW = 'RENEW',
  VARY = 'VARY',
}

interface ICaseAdd {
  LegalEntityid: string;
  licenceId?: string;
  caseType: EAddCaseType;
  submitForm: (data: any) => void;
  isFromLE: boolean;
  type: string | null;
  backLink: () => void;
  isVary?: boolean;
  isRenew?: boolean;
}

const getLicenceAuthorityId = (
  licencingAuthority: IDictionary[],
  primaryWeaponLicencingAuthorityUniqueId: string,
) => {
  if (
    licencingAuthority.find(
      (item: IDictionary) => item.uniqueId === primaryWeaponLicencingAuthorityUniqueId,
    )
  ) {
    return primaryWeaponLicencingAuthorityUniqueId;
  } else {
    return licencingAuthority[0].uniqueId.toString();
  }
};

const CaseAddForm = ({
  LegalEntityid,
  licenceId,
  caseType,
  submitForm,
  isFromLE,
  type,
  backLink,
  isVary = false,
  isRenew = false,
}: ICaseAdd) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLicence, setSelectedLicence] = useState<
    IWeaponLicenceTemplates | undefined
  >();
  const { t } = useTranslations();
  const { id = '' } = useParams<{ id: string }>();
  const { schema } = useFormSchema(caseType);
  const { clearAlert, samePageAlert } = useAlert();
  const { elRef, scrollToError } = useScroll();
  const { getTemplates, weaponLicenceTemplates, getOtherWorkers, caseOtherWorkersData } =
    useCase();
  const { getCaseRenewForm } = useRenewLicence();
  const { getCaseVaryForm } = useVaryLicence();
  const { account, getSelfAccountData } = useAccount();
  const { caseReprintLicencesData, getReprintLicencesData } = useCase();
  const {
    weaponLicencePaymentStatus,
    getWeaponLicencePaymentStatus,
    weaponLicencingAuthority,
    getWeaponLicencingAuthority,
    caseProcessTypes,
    getCaseProcessTypes,
    attachmentDocumentTypeData,
    getAttachmentDocumentTypeData,
  } = useDictionary();
  const [caseProcessTypesOptions, setCaseProcessTypesOptions] = useState<IDictionary[] | null>(
    null,
  );
  const [isLegalEntityLoading, setIsLegalEntityLoading] = useState<boolean>(isFromLE);
  const [weaponLicencingAuthorityForLE, setWeaponLicencingAuthorityForLe] = useState<
    IDictionary[] | null
  >(null);
  const [variationsData, setVariationsData] = useState<IVaryDataVariations[]>([]);
  const [variationsCheckedId, setVariationsCheckedId] = useState<string>('');
  const [varyData, setVaryData] = useState<IVaryData>();
  const [renewData, setRenewData] = useState<IRenewData>();
  const { shortDateFormat, currency, getProperties } = useGlobalProperty();
  const { selectedLanguage } = useLang();
  const { loadDictionaries, otherRequestTaskName } = useConfigurationLicence();
  const [otherRequestTaskNameOptions, setOtherRequestTaskNameOptions] = useState<
    IDictionary[]
  >([]);
  const [caseOtherWorkers, setCaseOtherWorkers] = useState<IFormElementOptionProp[]>([]);
  const [leHasLA, setLeHasLA] = useState(false);
  const [licences, setLicences] = useState<IFormElementOptionProp[]>([]);

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.otherRequestTaskName]);
      getProperties();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (otherRequestTaskName) {
      setOtherRequestTaskNameOptions(otherRequestTaskName);
    }
  }, [otherRequestTaskName]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    getSelfAccountData();
  }, []);

  const renderDate = (date: string) => formatDate(date, shortDateFormat);
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
  } = useForm<ICaseFormValues>({
    defaultValues: initialCaseFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<ICaseFormValues>,
  });

  const licenceCodeNameWatch = watch('licenceCodeName');
  const processTypeUniqueIdWatch = watch('processTypeUniqueId');
  const taskNameUniqueIdWatch = watch('taskNameUniqueId');
  const licenceAuthorityUniqueIdWatch = watch('licenceAuthorityUniqueId');
  const documentWillUploadLetterWatch = watch('documentWillUploadLetter');

  useEffect(() => {
    if (LegalEntityid && taskNameUniqueIdWatch === ECaseTaskNames.REPRINT_LICENCE) {
      getReprintLicencesData(LegalEntityid);
    }
  }, [LegalEntityid, taskNameUniqueIdWatch]);

  useEffect(() => {
    const data = (caseReprintLicencesData ?? []).map((licence) => ({
      label: `${licence.licenceName}, ${licence.licenceNumber}`,
      value: licence.uniqueId,
    }));
    setLicences(data);
  }, [caseReprintLicencesData]);

  useEffect(() => {
    if (variationsCheckedId && isVary) {
      setValue('applicationProcessUniqueId', variationsCheckedId);
    }
  }, [variationsCheckedId]);

  useEffect(() => {
    if (!weaponLicencePaymentStatus) {
      getWeaponLicencePaymentStatus();
    }
    if (!weaponLicencingAuthority) {
      getWeaponLicencingAuthority();
    }
    if (!caseProcessTypes) {
      getCaseProcessTypes();
    }
    if (!attachmentDocumentTypeData) {
      getAttachmentDocumentTypeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getWeaponLicenceTemplatesValues = async (
    val?: any,
  ): Promise<IFormElementOptionProp[]> => {
    if (val.length >= 1) {
      const result = await getTemplates(LegalEntityid, val);
      return result.map((item) => ({
        label: `${item.code} ${item.name}`,
        value: item.uniqueId,
      }));
    }
    return [];
  };

  useEffect(() => {
    (async function changeLicence() {
      if (caseType === EAddCaseType.APPLICATION) {
        if (licenceCodeNameWatch) {
          const selected = weaponLicenceTemplates.find(
            (item) => item.uniqueId === licenceCodeNameWatch,
          );
          reset({ ...getValues(), documentWillUploadLetter: null, documents: null });
          setSelectedLicence(selected);
        } else {
          reset({ ...getValues(), documentWillUploadLetter: null, documents: null });
          setSelectedLicence(undefined);
        }
      }
    })();
  }, [licenceCodeNameWatch]);

  useEffect(() => {
    if (LegalEntityid && weaponLicencingAuthority) {
      const isAssignedLE = account?.assignedLegalIds.includes(id as string);
      if (caseType == EAddCaseType.RENEW && licenceId) {
        (async function getDataInit() {
          setIsLegalEntityLoading(true);
          const renewData = await getCaseRenewForm(licenceId);
          if (renewData) {
            setLeHasLA(
              !!renewData?.legalEntity.primaryLicenceAuthorityUniqueId ||
                !!renewData?.legalEntity.secondaryLicenceAuthorityUniqueId,
            );

            setRenewData(renewData);
            setValue('legalUniqueId', LegalEntityid);
            setValue('legalName', renewData?.legalEntity.legalName);
            setValue('processTypeUniqueId', ECaseProcessTypes.RENEWAL);
            setValue(
              'licenceCodeName',
              `${renewData?.licence.licenceCode} ${renewData?.licence.licenceName}`,
            );
            setSelectedLicence({
              uniqueId: licenceId,
              code: renewData.licence.licenceCode,
              name: renewData.licence.licenceName,
              internalDescription: renewData.licence.internalDescription,
              documents: renewData.licenceTemplateDocuments,
            });
            const licencingAuthority = weaponLicencingAuthority
              ?.filter(
                (item) =>
                  item.uniqueId === renewData.legalEntity.primaryLicenceAuthorityUniqueId ||
                  item.uniqueId === renewData.legalEntity.secondaryLicenceAuthorityUniqueId,
              )
              .filter(
                (item) =>
                  account?.licenceAuthorityIds.includes(item.uniqueId.toString()) ||
                  isAssignedLE,
              );

            if (licencingAuthority.length) {
              setValue(
                'licenceAuthorityUniqueId',
                getLicenceAuthorityId(
                  licencingAuthority,
                  renewData.legalEntity.primaryLicenceAuthorityUniqueId,
                ),
              );
            }
            if (licencingAuthority) {
              setWeaponLicencingAuthorityForLe(licencingAuthority);
            }
          }
          setIsLegalEntityLoading(false);
        })();
      } else if (caseType === EAddCaseType.VARY && licenceId) {
        (async function getDataInit() {
          setIsLegalEntityLoading(true);
          const varyData = await getCaseVaryForm(licenceId);
          if (varyData) {
            setLeHasLA(
              !!varyData?.legalEntity.primaryLicenceAuthorityUniqueId ||
                !!varyData?.legalEntity.secondaryLicenceAuthorityUniqueId,
            );

            setVaryData(varyData);
            setValue('legalUniqueId', LegalEntityid);
            setValue('legalName', varyData?.legalEntity?.legalName);
            setValue('processTypeUniqueId', ECaseProcessTypes.VARIATION);
            setValue(
              'licenceCodeName',
              `${varyData?.licence.licenceCode} ${varyData?.licence.licenceName}`,
            );
            const licencingAuthority = weaponLicencingAuthority
              ?.filter(
                (item) =>
                  item.uniqueId === varyData.legalEntity.primaryLicenceAuthorityUniqueId ||
                  item.uniqueId === varyData.legalEntity.secondaryLicenceAuthorityUniqueId,
              )
              .filter(
                (item) =>
                  account?.licenceAuthorityIds.includes(item.uniqueId.toString()) ||
                  isAssignedLE,
              );
            if (licencingAuthority) {
              setWeaponLicencingAuthorityForLe(licencingAuthority);
            }
            if (licencingAuthority.length) {
              setValue(
                'licenceAuthorityUniqueId',
                getLicenceAuthorityId(
                  licencingAuthority,
                  varyData.legalEntity.primaryLicenceAuthorityUniqueId,
                ),
              );
            }
            if (varyData.variations) {
              setVariationsData(varyData.variations);
            }
          }
          setIsLegalEntityLoading(false);
        })();
      } else if (caseType === EAddCaseType.APPLICATION) {
        if (type === ELegalEntityTypes.individual) {
          (async function getLegalEntityDataInit() {
            setIsLegalEntityLoading(true);
            if (LegalEntityid) {
              const response = await getLegalEntity(LegalEntityid);
              if (response) {
                setLeHasLA(
                  !!response?.primaryWeaponLicencingAuthorityUniqueId ||
                    !!response?.secondaryWeaponLicencingAuthorityUniqueId,
                );

                setValue('legalUniqueId', response?.uniqueId);
                setValue('legalName', `${response?.firstName} ${response.lastName}`);
                const licencingAuthority = weaponLicencingAuthority
                  ?.filter(
                    (item) =>
                      item.uniqueId === response.primaryWeaponLicencingAuthorityUniqueId ||
                      item.uniqueId === response.secondaryWeaponLicencingAuthorityUniqueId,
                  )
                  .filter(
                    (item) =>
                      account?.licenceAuthorityIds.includes(item.uniqueId.toString()) ||
                      isAssignedLE,
                  );
                if (licencingAuthority.length) {
                  setValue(
                    'licenceAuthorityUniqueId',
                    getLicenceAuthorityId(
                      licencingAuthority,
                      response.primaryWeaponLicencingAuthorityUniqueId as string,
                    ),
                  );
                }
                if (licencingAuthority) {
                  setWeaponLicencingAuthorityForLe(licencingAuthority);
                }
              }
              setIsLegalEntityLoading(false);
            }
          })();
        } else if (type === ELegalEntityTypes.organisation) {
          (async function getLegalEntityDataInit() {
            setIsLegalEntityLoading(true);
            if (LegalEntityid) {
              const response = await getOrgLegalEntity(LegalEntityid);
              if (response) {
                setLeHasLA(
                  !!response?.primaryWeaponLicencingAuthorityUniqueId ||
                    !!response?.secondaryWeaponLicencingAuthorityUniqueId,
                );

                setValue('legalUniqueId', response?.uniqueId);
                setValue('legalName', response?.organizationName);

                const licencingAuthority = weaponLicencingAuthority
                  ?.filter(
                    (item) =>
                      item.uniqueId === response.primaryWeaponLicencingAuthorityUniqueId ||
                      item.uniqueId === response.secondaryWeaponLicencingAuthorityUniqueId,
                  )
                  .filter(
                    (item) =>
                      account?.licenceAuthorityIds.includes(item.uniqueId.toString()) ||
                      isAssignedLE,
                  );
                if (licencingAuthority.length) {
                  setValue(
                    'licenceAuthorityUniqueId',
                    getLicenceAuthorityId(
                      licencingAuthority,
                      response.primaryWeaponLicencingAuthorityUniqueId,
                    ),
                  );
                }
                if (licencingAuthority) {
                  setWeaponLicencingAuthorityForLe(licencingAuthority);
                }
              }
              setIsLegalEntityLoading(false);
            }
          })();
        }
      }
    }
  }, [LegalEntityid, weaponLicencingAuthority, account]);

  useEffect(() => {
    (async () => {
      scrollToError(errors);
    })();
  }, [errors]);

  const submitFormData = async (data: any) => {
    setIsLoading(true);
    await submitForm(data);
    setIsLoading(false);
  };

  const selectedVariation = (selectedData: any) => {
    setVariationsCheckedId(selectedData.id);
    setSelectedLicence({
      uniqueId: licenceId ?? '',
      code: varyData?.licence.licenceCode ?? '',
      name: varyData?.licence.licenceName ?? '',
      internalDescription: varyData?.licence.internalDescription ?? '',
      documents: selectedData.documents,
    });
    reset({ ...getValues(), documentWillUploadLetter: null, documents: null });
  };

  useEffect(() => {
    if (caseType === EAddCaseType.APPLICATION && caseProcessTypes) {
      setCaseProcessTypesOptions(
        caseProcessTypes?.filter(
          (item) =>
            item.uniqueId === ECaseProcessTypes.APPLICATION ||
            item.uniqueId === ECaseProcessTypes.OTHER_REQUEST,
        ),
      );
    } else {
      setCaseProcessTypesOptions(caseProcessTypes);
    }
  }, [caseType, caseProcessTypes]);

  useEffect(() => {
    if (
      processTypeUniqueIdWatch === ECaseProcessTypes.OTHER_REQUEST &&
      licenceAuthorityUniqueIdWatch
    ) {
      getOtherWorkers(licenceAuthorityUniqueIdWatch);
    }
  }, [processTypeUniqueIdWatch, licenceAuthorityUniqueIdWatch]);

  useEffect(() => {
    if (caseOtherWorkersData?.length) {
      const data = caseOtherWorkersData.map((worker) => ({
        label: `${worker.firstName} ${worker.lastName}, ${worker.displayUniqueId}`,
        value: worker.uniqueId,
      }));
      setCaseOtherWorkers(data);
    }
  }, [caseOtherWorkersData]);

  return (
    <>
      {samePageAlert && (
        <Box sx={{ marginY: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}
      {!leHasLA && !isLegalEntityLoading && (
        <Box sx={{ marginY: 2 }}>
          <Alert
            text={t('addCasePage.legalWithoutLA.warning.text')}
            variant={EAlertVariants.warning}
          />
        </Box>
      )}
      {isLegalEntityLoading && <Loader isLoading={true} />}
      {!isLegalEntityLoading && (
        <form onSubmit={handleSubmit(submitFormData)} id="add_case_form">
          <Grid container ref={elRef} sx={{ rowGap: 1 }}>
            <Grid container size={{ xs: 12 }} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12 }}>
                <StyledValueFormHeader>
                  {t(Elabels.caseGeneralInformation)}
                </StyledValueFormHeader>
              </Grid>
            </Grid>
            <Grid container size={{ xs: 12 }}>
              {caseType === EAddCaseType.VARY && varyData && (
                <StyledInternalDescription
                  container
                  size={{ xs: 12 }}
                  spacing={2}
                  style={{ marginBottom: '22px', marginTop: '2px' }}
                >
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(Elabels.licenceNumber)}
                    </Typography>
                    <Typography variant="body2">{varyData.licence.licenceNumber}</Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(Elabels.startEndDate)}
                    </Typography>
                    <Typography variant="body2">
                      {`${renderDate(varyData.licence.startDate)} - ${renderDate(varyData.licence.endDate)}`}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(Elabels.licenceAuthority)}
                    </Typography>
                    <Typography variant="body2">
                      {findInDictionary(
                        varyData.licence.licenceAuthorityUniqueId,
                        weaponLicencingAuthorityForLE,
                      )}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(Elabels.licenceCodeName)}
                    </Typography>
                    <Typography variant="body2">{`${varyData.licence.licenceCode} ${varyData.licence.licenceName}`}</Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(Elabels.procesType)}
                    </Typography>
                    <Typography variant="body2">
                      {findInDictionary(ECaseProcessTypes.VARIATION, caseProcessTypes)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(Elabels.internalDescription)}
                    </Typography>
                    <Typography variant="body2">
                      {getHtmlText(varyData.licence.internalDescription)}
                    </Typography>
                  </Grid>
                </StyledInternalDescription>
              )}
              {caseType === EAddCaseType.RENEW && renewData && (
                <StyledInternalDescription
                  container
                  size={{ xs: 12 }}
                  spacing={3}
                  style={{ marginBottom: '22px', marginTop: '2px' }}
                >
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(Elabels.licenceNumber)}
                    </Typography>
                    <Typography variant="body2">{renewData.licence.licenceNumber}</Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(Elabels.startEndDate)}
                    </Typography>
                    <Typography variant="body2">
                      {`${renderDate(renewData.licence.startDate)} - ${renderDate(renewData.licence.endDate)}`}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(Elabels.licenceAuthority)}
                    </Typography>
                    <Typography variant="body2">
                      {findInDictionary(
                        renewData.licence.licenceAuthorityUniqueId,
                        weaponLicencingAuthorityForLE,
                      )}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(Elabels.licenceCodeName)}
                    </Typography>
                    <Typography variant="body2">{`${renewData.licence.licenceCode} ${renewData.licence.licenceName}`}</Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(Elabels.procesType)}
                    </Typography>
                    <Typography variant="body2">
                      {findInDictionary(ECaseProcessTypes.RENEWAL, caseProcessTypes)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(Elabels.internalDescription)}
                    </Typography>
                    <Typography variant="body2">
                      {getHtmlText(renewData.licence.internalDescription)}
                    </Typography>
                  </Grid>
                </StyledInternalDescription>
              )}
              <Grid container size={{ xs: 12 }} columnSpacing={2.5}>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <FormInput
                    name="legalName"
                    label={t(Elabels.legalEntityName)}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    disabled
                    id="case_add_form_legal_name-input"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <FormSelect
                    options={mapDictionaryToOptionProp(weaponLicencingAuthorityForLE)}
                    name="licenceAuthorityUniqueId"
                    setValue={setValue}
                    required
                    label={t(Elabels.licenceAuthority)}
                    control={control}
                    errors={errors}
                    disabled={
                      !!(
                        weaponLicencingAuthorityForLE &&
                        weaponLicencingAuthorityForLE?.length <= 1
                      ) || !leHasLA
                    }
                    id="case_add_form_licence_authority_unique_id-select"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  {processTypeUniqueIdWatch &&
                    processTypeUniqueIdWatch !== ECaseProcessTypes.OTHER_REQUEST && (
                      <FormSelect
                        options={mapDictionaryToOptionProp(weaponLicencePaymentStatus)}
                        name="paymentStatusUniqueId"
                        setValue={setValue}
                        required
                        label={t(Elabels.paymentStatus)}
                        control={control}
                        errors={errors}
                        disabled={!leHasLA}
                        id="case_add_form_payment_status_unique_id-select"
                      />
                    )}
                </Grid>
                {!isVary && !isRenew && (
                  <>
                    <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                      <FormSelect
                        options={mapDictionaryToOptionProp(caseProcessTypesOptions)}
                        name="processTypeUniqueId"
                        setValue={setValue}
                        required
                        label={t(Elabels.procesType)}
                        control={control}
                        errors={errors}
                        disabled={caseType !== EAddCaseType.APPLICATION || !leHasLA}
                        id="case_add_form_process_type_unique_id-select"
                      />
                    </Grid>
                    {processTypeUniqueIdWatch &&
                      processTypeUniqueIdWatch === ECaseProcessTypes.APPLICATION && (
                        <Grid size={{ xs: 12, md: 12, lg: 8 }}>
                          <FormAutocomplete
                            name="licenceCodeName"
                            label={t(Elabels.licenceCodeName)}
                            control={control}
                            errors={errors}
                            loadOptions={(val) => getWeaponLicenceTemplatesValues(val)}
                            required
                            charsLimit={1}
                            disabled={!leHasLA}
                          />
                        </Grid>
                      )}
                    {processTypeUniqueIdWatch &&
                      processTypeUniqueIdWatch === ECaseProcessTypes.OTHER_REQUEST && (
                        <>
                          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                            <FormSelect
                              options={mapDictionaryToOptionProp(otherRequestTaskNameOptions)}
                              name="taskNameUniqueId"
                              label={t(Elabels.taskName)}
                              control={control}
                              errors={errors}
                              setValue={setValue}
                              required
                              disabled={!leHasLA}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                            {licenceAuthorityUniqueIdWatch && (
                              <FormSelect
                                options={caseOtherWorkers}
                                name="workerUniqueId"
                                label={t(Elabels.assignedWorker)}
                                control={control}
                                errors={errors}
                                setValue={setValue}
                                placeholder={t('formSelect.select.placeholder')}
                                disabled={!leHasLA}
                              />
                            )}
                          </Grid>
                          {taskNameUniqueIdWatch === ECaseTaskNames.REPRINT_LICENCE && (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                              <FormSearchSelect
                                options={licences}
                                name="weaponLicenceUniqueId"
                                label={t(Elabels.reprintLicenceLicence)}
                                control={control}
                                errors={errors}
                                required
                                onSearchChange={(search) =>
                                  getReprintLicencesData(LegalEntityid, search)
                                }
                                id="reprint_licence_form_weapon_licence_unique_id-select"
                              />
                            </Grid>
                          )}
                          <Grid size={{ xs: 12 }}>
                            <FormInput
                              name="description"
                              label={t(Elabels.description)}
                              setValue={setValue}
                              control={control}
                              errors={errors}
                              multiline={true}
                              minRows={3}
                              charsLimit={1000}
                              showCounter
                              required
                              disabled={!leHasLA}
                            />
                          </Grid>
                        </>
                      )}
                  </>
                )}

                <Grid size={{ xs: 12 }}>
                  {caseType === EAddCaseType.APPLICATION && selectedLicence && (
                    <StyledInternalDescription
                      style={{ marginBottom: '22px', marginTop: '2px' }}
                    >
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(Elabels.internalDescription)}
                      </Typography>
                      <Typography variant="body2">
                        {getHtmlText(selectedLicence?.internalDescription)}
                      </Typography>
                    </StyledInternalDescription>
                  )}
                </Grid>
                {!isVary &&
                  processTypeUniqueIdWatch &&
                  processTypeUniqueIdWatch !== ECaseProcessTypes.OTHER_REQUEST && (
                    <Grid size={{ xs: 12 }}>
                      <FileDropzoneContainer
                        label={t(Elabels.applicationFile)}
                        name="applicationFiles"
                        fileName="applicationsFilenames"
                        required
                        multiple
                        errors={errors}
                        setValue={setValue}
                        disabledInputName={false}
                        allowedType={[
                          EMIMETypes.JPEG,
                          EMIMETypes.JPG,
                          EMIMETypes.PNG,
                          EMIMETypes.PDF,
                        ]}
                      />
                    </Grid>
                  )}
              </Grid>
            </Grid>

            {isVary && variationsData && (
              <Grid
                size={{ xs: 12 }}
                sx={{ display: 'flex', flexDirection: 'column', gap: '4px', mb: 2 }}
              >
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {`${t(Elabels.selectVariation)}*`}
                </Typography>
                <Table
                  columns={variationsTableColumns({
                    handleCheck: selectedVariation,
                    checkedId: variationsCheckedId,
                    currency,
                  })}
                  dataSource={mapVariationsToDataSource(variationsData)}
                  id="case_add_form_variations-table"
                />
                {errors['applicationProcessUniqueId']?.message && (
                  <FormErrorLabel label={errors['applicationProcessUniqueId']?.message} />
                )}
              </Grid>
            )}
            {isVary && (
              <Grid size={{ xs: 12 }} container>
                <Grid size={{ xs: 12 }}>
                  <FormInput
                    name="description"
                    label={t(Elabels.description)}
                    showCounter
                    charsLimit={1000}
                    setValue={setValue}
                    control={control}
                    errors={errors}
                    multiline={true}
                    minRows={3}
                    required
                    id="case_add_form_description-input"
                    disabled={!leHasLA}
                  />
                </Grid>
              </Grid>
            )}

            <Grid
              size={{ xs: 12 }}
              sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
            >
              {!!selectedLicence?.documents.length && (
                <DocumentsForm
                  uniqueId={selectedLicence?.uniqueId}
                  documents={selectedLicence?.documents}
                  documentWillUploadLetterWatch={documentWillUploadLetterWatch}
                  attachmentDocumentTypeData={attachmentDocumentTypeData}
                  errors={errors}
                  setValue={setValue}
                  control={control}
                />
              )}
            </Grid>

            <Grid
              container
              size={{ xs: 12 }}
              columnSpacing={2.5}
              sx={{ rowGap: 1, marginTop: 4 }}
            >
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Button
                  type="submit"
                  fullWidth
                  label={t('general.save.button')}
                  id="save-button"
                  disabled={isLoading || !leHasLA}
                  variant={EButtonVariants.contained}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Button
                  fullWidth
                  label={t('general.cancel.button')}
                  id="cancel-button"
                  variant={EButtonVariants.outlined}
                  disabled={isLoading}
                  onClick={backLink}
                />
              </Grid>
            </Grid>
            <Grid sx={{ marginTop: 1 }}>
              <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
            </Grid>
          </Grid>
        </form>
      )}
    </>
  );
};

export default CaseAddForm;
